import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { KaminoVaultPosition } from "@stepfinance/vaults";
import { KaminoPositionFetcher } from "@stepfinance/vaults";
import { useEffect, useMemo, useState } from "react";

import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useMarkets } from "../../contexts/market";
import { getTokenByMint } from "../../utils/tokenInfo";
import { useVaultsQuery } from "../queries/useVaultsQuery";

export const useKaminoVaults = (): KaminoVaultPosition[] => {
  const { publicKey, connected } = useEnrichedWallet();
  const { connection } = useConnection();
  const [positions, setPositions] = useState<KaminoVaultPosition[]>([]);
  const {
    data: { kaminoVaults },
    isFetched,
  } = useVaultsQuery();
  const { marketsInitialised } = useMarkets();

  const fetcher = useMemo(() => {
    if (!publicKey) {
      return undefined;
    }
    return new KaminoPositionFetcher(publicKey, connection, kaminoVaults);
  }, [connection, kaminoVaults, publicKey]);

  useEffect(() => setPositions([]), [connected]);

  useEffect(() => {
    if (!publicKey || !fetcher || !isFetched || !marketsInitialised) {
      return;
    }
    fetcher.fetchPositions(getTokenByMint).then(setPositions).catch(captureException);
  }, [fetcher, isFetched, publicKey, marketsInitialised]);

  return positions;
};
